import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Chip,
  TextField,
  Dialog,
  List,
  Alert,
  CircularProgress,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  Send,
  Email as EmailIcon,
  Delete,
  CheckCircle,
  Autorenew,
  ExpandMore,
  Edit,
} from '@mui/icons-material';
import { praAPI } from '../services/api';
import LettersTabView from './LettersTabView';
import SenderAccountSendDialog from './SenderAccountSendDialog';

const statusColors = {
  draft: 'default',
  pending_review: 'info',
  sent: 'primary',
  acknowledged: 'info',
  fulfilled: 'success',
  partially_fulfilled: 'warning',
  denied: 'error',
  overdue: 'error',
};

const statusOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'pending_review', label: 'Pending Review' },
  { value: 'sent', label: 'Sent' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'fulfilled', label: 'Fulfilled' },
  { value: 'partially_fulfilled', label: 'Partially Fulfilled' },
  { value: 'denied', label: 'Denied' },
  { value: 'overdue', label: 'Overdue' },
];

const RequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [attachedEmails, setAttachedEmails] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    const fetchRequest = async () => {
      try {
        const response = await praAPI.getRequest(id);
        setRequest(response.data);
        setNotes(response.data.notes || '');
      } catch (error) {
        console.error('Error fetching request:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRequest();
  }, [id]);

  useEffect(() => {
    const fetchAttachedEmails = async () => {
      setLoadingEmails(true);
      try {
        const response = await praAPI.getRequestEmails(id);
        setAttachedEmails(response.data);
      } catch (error) {
        console.error('Error fetching attached emails:', error);
      } finally {
        setLoadingEmails(false);
      }
    };

    fetchAttachedEmails();
    const interval = setInterval(fetchAttachedEmails, 10000);
    return () => clearInterval(interval);
  }, [id]);


  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await praAPI.updateRequest(id, { notes });
      setRequest((prev) => ({ ...prev, notes }));
    } catch (error) {
      alert('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleDetachEmail = async (messageId) => {
    try {
      await praAPI.detachEmailFromRequest(id, messageId);
      setAttachedEmails((prev) => prev.filter((e) => e.message_id !== messageId));
    } catch (error) {
      alert('Failed to detach email');
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      await praAPI.updateRequest(id, { status: newStatus });
      setRequest((prev) => ({ ...prev, status: newStatus }));
    } catch (error) {
      alert('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getRequestTitle = () => {
    if (!request) return '';
    if (request.subject) return request.subject;
    if (request.natural_language_input) {
      return request.natural_language_input.length > 120
        ? request.natural_language_input.substring(0, 120) + '…'
        : request.natural_language_input;
    }
    return `Request #${request.id}`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!request) {
    return <Alert severity="error">Request not found</Alert>;
  }

  const accordionSx = {
    border: '1px solid #e0ddd6',
    borderRadius: '8px !important',
    mb: 1.5,
    '&:before': { display: 'none' },
    boxShadow: 'none',
  };

  const summaryLabelSx = {
    fontFamily: 'Georgia, serif',
    fontWeight: 700,
    fontSize: '0.95rem',
    color: '#00075e',
  };

  const metaLabelSx = {
    fontFamily: '"Helvetica Neue", Arial, sans-serif',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#838996',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    minWidth: 110,
    pt: 0.15,
    flexShrink: 0,
  };

  const metaValueSx = {
    fontFamily: '"Helvetica Neue", Arial, sans-serif',
    fontSize: '0.9rem',
    color: '#00075e',
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/requests')}
        sx={{ mb: 2, color: '#838996', fontFamily: '"Helvetica Neue", Arial' }}
      >
        Back to Requests
      </Button>

      {/* Page header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
          mb: 3,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              fontFamily: 'Georgia, serif',
              fontWeight: 700,
              fontSize: '1.55rem',
              color: '#00075e',
              lineHeight: 1.3,
              mb: 0.5,
            }}
          >
            {getRequestTitle()}
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Helvetica Neue", Arial, sans-serif',
              fontSize: '0.85rem',
              color: '#838996',
            }}
          >
            {request.destination || 'Unknown destination'} &middot; Created {formatDate(request.created_at)}
          </Typography>
        </Box>
        <Chip
          label={(request.status || '').replace(/_/g, ' ').toUpperCase()}
          color={statusColors[request.status] || 'default'}
          sx={{ fontFamily: '"Helvetica Neue", Arial', fontWeight: 700, mt: 0.5 }}
        />
      </Box>

      {/* Accordion 1: Overview & Actions */}
      <Accordion defaultExpanded sx={accordionSx}>
        <AccordionSummary
          expandIcon={<ExpandMore sx={{ color: '#838996' }} />}
          sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center' } }}
        >
          <Typography sx={summaryLabelSx}>Overview &amp; Actions</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 3,
            }}
          >
            {/* Left: metadata */}
            <Box>
              {[
                { label: 'Destination', value: request.destination || '—' },
                { label: 'Sent', value: formatDate(request.sent_date) },
                { label: 'Ack. Due', value: formatDate(request.acknowledgment_due_date) },
                { label: 'Response', value: formatDate(request.response_received_date) },
                ...(request.email_to ? [{ label: 'To', value: request.email_to }] : []),
              ].map(({ label, value }) => (
                <Box key={label} sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                  <Typography sx={metaLabelSx}>{label}</Typography>
                  <Typography sx={metaValueSx}>{value}</Typography>
                </Box>
              ))}

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Typography sx={metaLabelSx}>Status</Typography>
                <FormControl size="small" disabled={updatingStatus} sx={{ minWidth: 180 }}>
                  <Select
                    value={request.status}
                    onChange={(e) => handleUpdateStatus(e.target.value)}
                    sx={{ fontFamily: '"Helvetica Neue", Arial', fontSize: '0.9rem' }}
                  >
                    {statusOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Right: actions */}
            <Box>
              {request.status === 'draft' ? (
                <>
                  <Button
                    variant="contained"
                    startIcon={<Send />}
                    onClick={() => setSendDialogOpen(true)}
                    disabled={!request.generated_request}
                    fullWidth
                    sx={{ mb: 1.5 }}
                  >
                    Send Request
                  </Button>
                  {!request.generated_request && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      Generate the PRA letter before sending.
                    </Alert>
                  )}
                </>
              ) : (
                <Alert severity="info">
                  {request.status === 'sent'
                    ? `Sent on ${formatDate(request.sent_date)}`
                    : `Status: ${request.status.replace(/_/g, ' ')}`}
                </Alert>
              )}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Accordion 2: Original Request Description */}
      <Accordion defaultExpanded sx={accordionSx}>
        <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#838996' }} />}>
          <Typography sx={summaryLabelSx}>Original Request Description</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography
            sx={{
              fontFamily: 'Georgia, serif',
              fontSize: '0.95rem',
              color: '#00075e',
              lineHeight: 1.75,
              whiteSpace: 'pre-wrap',
            }}
          >
            {request.natural_language_input}
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* Generated letter(s), rendered as email-client-style tabs */}
      {(request.generated_request || (request.is_split_request && request.split_parts?.length)) && (
        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{
              fontFamily: '"Helvetica Neue", Arial, sans-serif',
              fontSize: '0.72rem',
              fontWeight: 700,
              color: '#838996',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              mb: 1,
            }}
          >
            Generated Letter{request.is_split_request && request.split_parts?.length > 1 ? 's' : ''}
          </Typography>
          <LettersTabView request={request} />
        </Box>
      )}

      {/* Accordion 4: Notes */}
      <Accordion defaultExpanded={!!request.notes} sx={accordionSx}>
        <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#838996' }} />}>
          <Typography sx={summaryLabelSx}>Notes</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TextField
            fullWidth
            multiline
            minRows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add notes about this request, follow-up actions, exemptions claimed…"
            sx={{ mb: 1.5 }}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={handleSaveNotes}
            disabled={savingNotes || notes === (request.notes || '')}
            startIcon={
              savingNotes ? <CircularProgress size={14} color="inherit" /> : <Edit />
            }
          >
            {savingNotes ? 'Saving…' : 'Save Notes'}
          </Button>
        </AccordionDetails>
      </Accordion>

      {/* Accordion 5: Responses */}
      <Accordion defaultExpanded={attachedEmails.length > 0} sx={accordionSx}>
        <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#838996' }} />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={summaryLabelSx}>Responses</Typography>
            {attachedEmails.length > 0 && (
              <Chip
                label={attachedEmails.length}
                size="small"
                color="primary"
                sx={{ height: 18, fontSize: '0.7rem' }}
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {loadingEmails && attachedEmails.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : attachedEmails.length === 0 ? (
            <Alert severity="info">
              No responses yet. AI will automatically flag relevant emails from the Sender Inbox when
              you sync.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {attachedEmails.map((email) => (
                <Paper
                  key={email.id || email.message_id}
                  elevation={0}
                  sx={{ p: 2, border: '1px solid #e0ddd6', borderRadius: 1.5 }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box sx={{ flex: 1, mr: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                        <EmailIcon sx={{ fontSize: 16, color: '#2a52be', flexShrink: 0 }} />
                        <Typography
                          sx={{
                            fontFamily: 'Georgia, serif',
                            fontWeight: 700,
                            fontSize: '0.9rem',
                            color: '#00075e',
                          }}
                        >
                          {email.subject}
                        </Typography>
                        {email.confidence === 100 && (
                          <CheckCircle sx={{ fontSize: 14, color: '#4caf50', flexShrink: 0 }} />
                        )}
                        {email.confidence > 0 && email.confidence < 100 && (
                          <Autorenew sx={{ fontSize: 14, color: '#f57c00', flexShrink: 0 }} />
                        )}
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: '"Helvetica Neue", Arial, sans-serif',
                          fontSize: '0.78rem',
                          color: '#838996',
                          mb: 0.5,
                        }}
                      >
                        From: {email.from_address}
                        {email.attached_date &&
                          ` · ${new Date(email.attached_date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}`}
                      </Typography>
                      {email.confidence > 0 && email.confidence < 100 && (
                        <Chip
                          label={`AI: ${email.confidence}%`}
                          size="small"
                          color="warning"
                          sx={{ mb: 0.75, height: 18, fontSize: '0.7rem' }}
                        />
                      )}
                      {email.reasoning && (
                        <Typography
                          sx={{
                            fontFamily: '"Helvetica Neue", Arial, sans-serif',
                            fontSize: '0.75rem',
                            color: '#838996',
                            fontStyle: 'italic',
                            mb: 0.5,
                          }}
                        >
                          {email.reasoning}
                        </Typography>
                      )}
                      {(email.snippet || email.body) && (
                        <Typography
                          sx={{
                            fontFamily: 'Georgia, serif',
                            fontSize: '0.8rem',
                            color: '#838996',
                            backgroundColor: '#f9f8f5',
                            p: 1,
                            borderRadius: 1,
                          }}
                        >
                          {(email.snippet || email.body?.substring(0, 250))}
                          {(email.body?.length > 250 || (!email.body && email.snippet)) && '…'}
                        </Typography>
                      )}
                    </Box>
                    <Tooltip title="Detach email from request">
                      <IconButton
                        size="small"
                        onClick={() => handleDetachEmail(email.message_id)}
                        sx={{ color: '#c8c4bb', '&:hover': { color: '#d32f2f' } }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* App-wide sender-account chooser + send flow */}
      <SenderAccountSendDialog
        open={sendDialogOpen}
        requestId={id}
        onClose={() => setSendDialogOpen(false)}
        onSent={(updated) => {
          setRequest(updated);
          setSendDialogOpen(false);
        }}
      />
    </Container>
  );
};

export default RequestDetail;
